import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';
import { useBooking } from '../context/BookingContext';
import { cn } from '../lib/cn';
import { AudioToggle } from './AudioToggle';
import { InkButton } from './InkButton';
import { LanguageSwitcher } from './LanguageSwitcher';

const links = [
  { to: '/lavori', key: 'work' as const },
  { to: '/artisti', key: 'artists' as const },
  { to: '/studio', key: 'studio' as const },
];

export function Navbar() {
  const { t } = useTranslation();
  const { openBooking } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-40 transition-colors duration-300 text-ink-paper',
          scrolled
            ? 'bg-ink-base/85 backdrop-blur-md border-b border-ink-line/40'
            : 'bg-gradient-to-b from-ink-base/60 via-ink-base/20 to-transparent',
        )}
      >
        <nav className="flex items-center justify-between px-6 py-4 md:px-10 md:py-6">
          <NavLink
            to="/"
            className="font-display text-2xl tracking-display lowercase leading-none"
            aria-label={t('brand')}
          >
            {t('brand')}
          </NavLink>

          <ul className="hidden md:flex items-center gap-8 text-base lowercase font-display tracking-display">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  className={({ isActive }) =>
                    cn(
                      'relative py-1 transition-colors',
                      'after:absolute after:left-0 after:bottom-0 after:h-px after:bg-current after:transition-[width] after:duration-300',
                      isActive ? 'after:w-full' : 'after:w-0 hover:after:w-full',
                    )
                  }
                >
                  {t(`nav.${l.key}`)}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-4 md:gap-5">
            <AudioToggle />
            <LanguageSwitcher />
            <div className="hidden md:block">
              <InkButton as="button" size="md" onClick={() => openBooking()}>
                {t('nav.book')}
              </InkButton>
            </div>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('nav.menu')}
              className="md:hidden grid gap-1.5 p-2 -mr-2"
            >
              <span className="block h-px w-6 bg-current" />
              <span className="block h-px w-6 bg-current" />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {drawerOpen && (
          <motion.div
            className="md:hidden fixed inset-0 z-50 bg-ink-base text-ink-paper flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-line/40">
              <span className="font-display text-2xl tracking-display lowercase leading-none">
                {t('brand')}
              </span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label={t('nav.close')}
                className="size-10 border border-ink-line/60 hover:border-ink-paper transition-colors flex items-center justify-center"
              >
                <span aria-hidden className="text-xl leading-none">
                  ×
                </span>
              </button>
            </div>
            <ul className="flex-1 flex flex-col px-6 py-10 gap-2">
              {links.map((l, i) => (
                <motion.li
                  key={l.to}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.08 * i,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  <NavLink
                    to={l.to}
                    className="block font-display tracking-display text-5xl lowercase leading-none py-3"
                  >
                    {t(`nav.${l.key}`)}
                  </NavLink>
                </motion.li>
              ))}
            </ul>
            <div className="px-6 py-6 border-t border-ink-line/40 flex flex-col gap-4">
              <button
                type="button"
                onClick={() => {
                  setDrawerOpen(false);
                  openBooking();
                }}
                className="inline-flex items-center justify-between gap-3 px-6 py-4 bg-ink-paper text-ink-base text-sm uppercase tracking-[0.22em]"
              >
                <span>{t('nav.book')}</span>
                <span aria-hidden>→</span>
              </button>
              <LanguageSwitcher />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
